import { apiRequest, ApiRequestError } from '../api';
import { navigateTo, canGoBackInApp } from '../router';
import { clearSession, saveSession, state } from '../state';
import { renderUserBox } from '../app';
import { t } from '../i18n';
import type { TranslationKey } from '../i18n/types';

// ============================================================
// Личный профиль (/profile): смена ника, почты и пароля,
// а также переход в свой игровой профиль (карточку игрока).
// ============================================================

/** Ошибки сервера → локализованные тексты. */
const ERROR_KEYS: Record<string, TranslationKey> = {
    username_invalid: 'profile.error_username_invalid',
    username_taken: 'profile.error_username_taken',
    email_invalid: 'profile.error_email_invalid',
    email_taken: 'profile.error_email_taken',
    wrong_password: 'profile.error_wrong_password',
    password_short: 'profile.error_password_short',
};

function showStatus(
    el: HTMLElement | null,
    text: string,
    kind: 'success' | 'error'
): void {
    if (!el) return;
    el.textContent = text;
    el.classList.remove('hidden', 'is-success', 'is-error');
    el.classList.add(kind === 'success' ? 'is-success' : 'is-error');
}

function clearStatus(el: HTMLElement | null): void {
    if (!el) return;
    el.textContent = '';
    el.classList.add('hidden');
    el.classList.remove('is-success', 'is-error');
}

/** Блокирует форму на время запроса. */
function setBusy(form: HTMLFormElement, busy: boolean, label: string): void {
    form
        .querySelectorAll<HTMLInputElement | HTMLButtonElement>('input, button')
        .forEach((el) => {
            el.disabled = busy;
        });
    const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]');
    if (!btn) return;
    if (busy) {
        if (btn.dataset.idleLabel === undefined) {
            btn.dataset.idleLabel = btn.textContent ?? '';
        }
        btn.textContent = label;
    } else if (btn.dataset.idleLabel !== undefined) {
        btn.textContent = btn.dataset.idleLabel;
    }
}

function errorText(err: unknown): string {
    if (err instanceof ApiRequestError && err.code) {
        const key = ERROR_KEYS[err.code];
        if (key) return t(key);
    }
    return t('profile.error_generic');
}

/** Сохраняет обновлённого пользователя в сессию (шапка перерисуется сама). */
function persistUser(patch: { username?: string; email?: string }): void {
    const user = state.user;
    const token = state.token;
    if (!user || !token) return;
    saveSession(
        { ...user, ...patch },
        token,
        state.refreshToken ?? undefined,
        state.expiresAt
    );
}

// Слушатель смены сессии (выход/вход в другой вкладке) — снимаем при уходе.
let sessionListener: (() => void) | null = null;

export function mountProfile(): void {
    const screen = document.getElementById('screen-profile');
    if (!screen) return;

    document.querySelectorAll<HTMLElement>('.screen').forEach((el) => {
        el.classList.toggle('hidden', el.id !== 'screen-profile');
    });

    // Гость не может менять профиль — уводим на форму входа.
    if (!state.user || !state.token) {
        navigateTo('/auth', true);
        return;
    }

    const backBtn = document.getElementById('btn-back-profile');
    if (backBtn) {
        const fresh = backBtn.cloneNode(true) as HTMLElement;
        backBtn.replaceWith(fresh);
        fresh.addEventListener('click', () => {
            if (canGoBackInApp()) window.history.back();
            else navigateTo('/', true);
        });
    }

    // «Выйти» переехало сюда из шапки
    bindLogout();

    fillCurrentValues();
    bindUsernameForm();
    bindEmailForm();
    bindPasswordForm();
    bindPasswordToggles(screen);
    renderGameProfile();

    // Разлогинились в другой вкладке — уводим со страницы профиля.
    if (sessionListener) window.removeEventListener('session:changed', sessionListener);
    sessionListener = () => {
        if (!state.user) navigateTo('/auth', true);
    };
    window.addEventListener('session:changed', sessionListener);
}

export function unmountProfile(): void {
    if (sessionListener) {
        window.removeEventListener('session:changed', sessionListener);
        sessionListener = null;
    }
}

/** Выход из аккаунта (перенесён из шапки в личный профиль). */
function bindLogout(): void {
    const btn = document.getElementById('btn-profile-logout');
    if (!btn) return;

    btn.onclick = () => {
        void (async () => {
            try {
                await apiRequest('/api/auth/logout', {
                    method: 'POST',
                    token: state.token,
                });
            } catch {
                /* даже если сервер не ответил — локально выходим */
            }
            // Снимаем слушатель, чтобы не уводить на /auth, и уходим на главную.
            if (sessionListener) {
                window.removeEventListener('session:changed', sessionListener);
                sessionListener = null;
            }
            clearSession();
            renderUserBox();
            navigateTo('/', true);
        })();
    };
}

/** Подставляет текущие ник и почту в поля. */
function fillCurrentValues(): void {
    const usernameInput = document.getElementById(
        'profile-username'
    ) as HTMLInputElement | null;
    const emailInput = document.getElementById(
        'profile-email'
    ) as HTMLInputElement | null;

    if (usernameInput) usernameInput.value = state.user?.username ?? '';
    if (emailInput) emailInput.value = state.user?.email ?? '';

    ['profile-username-status', 'profile-email-status', 'profile-password-status'].forEach(
        (id) => clearStatus(document.getElementById(id))
    );

    const passwordForm = document.getElementById(
        'form-profile-password'
    ) as HTMLFormElement | null;
    passwordForm?.reset();
}

function bindUsernameForm(): void {
    const form = document.getElementById(
        'form-profile-username'
    ) as HTMLFormElement | null;
    if (!form) return;

    form.onsubmit = (e) => {
        e.preventDefault();
        void (async () => {
            const status = document.getElementById('profile-username-status');
            const input = form.querySelector<HTMLInputElement>('input[name="username"]');
            const username = input?.value.trim() ?? '';
            clearStatus(status);

            if (username === (state.user?.username ?? '')) {
                showStatus(status, t('profile.saved'), 'success');
                return;
            }

            setBusy(form, true, t('profile.saving'));
            try {
                const res = await apiRequest<{ user: { username: string } }>(
                    '/api/auth/username',
                    { method: 'PATCH', body: { username }, token: state.token }
                );
                persistUser({ username: res.user.username });
                if (input) input.value = res.user.username;
                showStatus(status, t('profile.saved'), 'success');
            } catch (err) {
                showStatus(status, errorText(err), 'error');
            } finally {
                setBusy(form, false, t('profile.saving'));
            }
        })();
    };
}

function bindEmailForm(): void {
    const form = document.getElementById(
        'form-profile-email'
    ) as HTMLFormElement | null;
    if (!form) return;

    form.onsubmit = (e) => {
        e.preventDefault();
        void (async () => {
            const status = document.getElementById('profile-email-status');
            const emailInput = form.querySelector<HTMLInputElement>('input[name="email"]');
            const passwordInput = form.querySelector<HTMLInputElement>(
                'input[name="current_password"]'
            );
            const email = emailInput?.value.trim() ?? '';
            const currentPassword = passwordInput?.value ?? '';
            clearStatus(status);

            if (!currentPassword) {
                showStatus(status, t('profile.error_wrong_password'), 'error');
                return;
            }

            setBusy(form, true, t('profile.saving'));
            try {
                const res = await apiRequest<{ user: { email: string } }>(
                    '/api/auth/email',
                    {
                        method: 'PATCH',
                        body: { email, current_password: currentPassword },
                        token: state.token,
                    }
                );
                persistUser({ email: res.user.email });
                if (emailInput) emailInput.value = res.user.email;
                form.reset();
                if (emailInput) emailInput.value = res.user.email;
                showStatus(status, t('profile.saved'), 'success');
            } catch (err) {
                showStatus(status, errorText(err), 'error');
            } finally {
                setBusy(form, false, t('profile.saving'));
            }
        })();
    };
}

function bindPasswordForm(): void {
    const form = document.getElementById(
        'form-profile-password'
    ) as HTMLFormElement | null;
    if (!form) return;

    form.onsubmit = (e) => {
        e.preventDefault();
        void (async () => {
            const status = document.getElementById('profile-password-status');
            const currentPassword =
                form.querySelector<HTMLInputElement>('input[name="current_password"]')
                    ?.value ?? '';
            const password =
                form.querySelector<HTMLInputElement>('input[name="password"]')?.value ?? '';
            const repeat =
                form.querySelector<HTMLInputElement>('input[name="password2"]')?.value ?? '';
            clearStatus(status);

            if (password.length < 6) {
                showStatus(status, t('profile.error_password_short'), 'error');
                return;
            }
            if (password !== repeat) {
                showStatus(status, t('profile.error_password_mismatch'), 'error');
                return;
            }
            if (!currentPassword) {
                showStatus(status, t('profile.error_wrong_password'), 'error');
                return;
            }

            setBusy(form, true, t('profile.saving'));
            try {
                await apiRequest('/api/auth/password', {
                    method: 'PATCH',
                    body: { current_password: currentPassword, password },
                    token: state.token,
                });
                form.reset();
                showStatus(status, t('profile.saved'), 'success');
            } catch (err) {
                showStatus(status, errorText(err), 'error');
            } finally {
                setBusy(form, false, t('profile.saving'));
            }
        })();
    };
}

/** Кнопки «показать пароль» внутри страницы профиля. */
function bindPasswordToggles(root: HTMLElement): void {
    root.onclick = (e) => {
        const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('.password-toggle');
        if (!btn) return;
        e.preventDefault();
        const input = btn.closest('.password-field')?.querySelector<HTMLInputElement>('input');
        if (!input) return;
        const show = input.type === 'password';
        input.type = show ? 'text' : 'password';
        btn.classList.toggle('is-visible', show);
        btn.title = show ? t('auth.hide_password') : t('auth.show_password');
    };
}

/** Ссылка на свою карточку игрока или подсказка, что профиль не привязан. */
function renderGameProfile(): void {
    const link = document.getElementById(
        'profile-game-link'
    ) as HTMLAnchorElement | null;
    const unlinked = document.getElementById('profile-game-unlinked');
    const name = state.user?.player_name;

    if (link) {
        if (name) {
            link.href = `/?player=${encodeURIComponent(name)}`;
            link.classList.remove('hidden');
        } else {
            link.classList.add('hidden');
        }
    }
    unlinked?.classList.toggle('hidden', Boolean(name));
}
